import { ILibraryFolder } from "@/api";
import { MoveToFolderModal } from "@/components/shared/move-to-folder";
import {
    RowActionsMenu,
    type RowAction,
} from "@/components/shared/RowActionsMenu";
import { libraryFolderToListItem } from "@/utils/library";
import { FolderInput, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import {
    isClientRootFolder,
    isForeignSharedSubject,
    isSharedRootFolder,
} from "../utils/folderTreeUtils";

import { DeleteFolderDialog } from "./DeleteFolderDialog";
import { EditFolderDialog } from "./EditFolderDialog";

interface FolderActionsMenuProps {
    folder: ILibraryFolder;
    onAction: () => void;
}

export const FolderActionsMenu = ({
    folder,
    onAction,
}: FolderActionsMenuProps) => {
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [moveOpen, setMoveOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Client-root folders anchor a workspace, so both Move and Delete dead-end
    // on them: the menu hides both (Edit is the only verb left) so neither
    // MoveToFolderModal nor DeleteFolderDialog opens onto a guaranteed failure.
    // Move is structurally immovable (hidden in Unit 8); Delete always 400s
    // ("Can't delete a client's root folder", the library_service guard since
    // Unit 1), so it's hidden here too (insurescope-afard). Root-only:
    // client-tagged SUBFOLDERS keep both (placement is free — the tag
    // re-derives from the destination on settle).
    const isClientRoot = isClientRootFolder(folder);

    // The Shared root can't be renamed, moved or deleted (the backend 400s
    // all three), and Edit is the rename verb here, so nothing is left to
    // offer: no trigger rather than an empty menu. Descendants carry
    // `is_shared` but not `is_shared_root`, so they keep every action.
    const isSharedRoot = isSharedRootFolder(folder);

    const actions = useMemo<RowAction[]>(
        () => [
            {
                key: "edit",
                label: "Edit",
                icon: <Pencil />,
                onSelect: () => setEditDialogOpen(true),
            },
            ...(isClientRoot
                ? []
                : [
                      {
                          key: "move",
                          label: "Move",
                          icon: <FolderInput />,
                          onSelect: () => setMoveOpen(true),
                      } satisfies RowAction,
                      {
                          key: "delete",
                          label: "Delete",
                          icon: <Trash2 />,
                          variant: "destructive",
                          onSelect: () => setDeleteDialogOpen(true),
                      } satisfies RowAction,
                  ]),
        ],
        [isClientRoot]
    );

    const handleMoveComplete = () => {
        setMoveOpen(false);
        onAction();
    };

    const handleEditComplete = () => {
        setEditDialogOpen(false);
        onAction();
    };

    const handleDeleteComplete = () => {
        setDeleteDialogOpen(false);
        onAction();
    };

    if (isSharedRoot || isForeignSharedSubject(folder)) {
        return null;
    }

    return (
        <>
            <RowActionsMenu actions={actions} />

            <EditFolderDialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                folder={folder}
                onSaved={handleEditComplete}
            />

            <DeleteFolderDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                folder={folder}
                onDeleted={handleDeleteComplete}
            />

            <MoveToFolderModal
                open={moveOpen}
                onClose={() => setMoveOpen(false)}
                items={[libraryFolderToListItem(folder)]}
                onMoved={handleMoveComplete}
            />
        </>
    );
};
